// Core React libraries import
import React, { useContext, useEffect, useRef, useState } from 'react';
import moment from 'moment';

// Core ANTD and 3rdparty libraries import
import { Row, Col, Form, Select, Typography, Alert, Button, Modal, Popover } from 'antd';
import {
    CloseOutlined,
    DeleteOutlined,
    ExpandAltOutlined,
    ExportOutlined,
    InfoCircleOutlined,
} from '@ant-design/icons';
import FullCalendar, {
    EventApi,
    EventClickArg,
    EventContentArg,
    EventInput,
} from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import debounce from 'lodash/debounce';

// EZ web-app import

// EZ web-app utils import
import { getCalendarEvents, syncAllIntegratedCalendars } from 'utils/ez_api/calendarAPIs';
import { getAllTeamMembers } from 'utils/ez_api/memberAPIs';
import { addNewTask, deleteTask, updateTask } from 'utils/ez_api/taskAPIs';
import { EZDateFormat, EZOverlaySpinner, EZSelectTransaction } from 'components';
import { showNotification } from 'utils/notificationUtil';
import { HomeContext } from 'context/EZContext';

// Page level styles
import { TAB_CATEGORY } from 'types/webAppTab';
import { EditTaskModalContent } from 'components/appPage/modals/taskEditDetail';
import { TransactionTaskItemType } from 'types/transaction';
import { AccountCompactDataType } from 'types/account';

type SelectedEventType = {
    eventTitle?: string;
    address?: string;
    contract_id?: string;

    start?: Date | null;
    end?: Date | null;
    extendedProps?: EventApi['extendedProps'];
};

const { Option, OptGroup } = Select;
const { Text } = Typography;

export function SubTab_CalendarLayout(props: any) {
    const HomeCtx = useContext(HomeContext);

    const CalendarState = {
        weekendsVisible: true,
        currentEvents: [],
    };

    const [SelectedEvent, setSelectedEvent] = useState<SelectedEventType>();
    const [Events, setEvents] = useState<EventInput[]>([]);
    const [CalendarDuration, setCalendarDuration] = useState<{
        start_date: string;
        end_date: string;
    }>();
    const [TransactionFilter, setTransactionFilter] = useState<number[]>([]);
    const [ColumnTypeFilter, setColumnTypeFilter] = useState<string>('');

    const [IsLoadingCalendarEvents, setIsLoadingCalendarEvents] = useState<boolean>(false);
    // const [IsLoadingEventDetail, setIsLoadingEventDetail] = useState<boolean>(false);
    const [TeamMembers, setTeamMembers] = useState<AccountCompactDataType[]>([]);

    const [form] = Form.useForm();
    const calendarRef = useRef<FullCalendar>(null);

    const handleEventClick = (clickInfo: EventClickArg) => {
        const payload: SelectedEventType = {};

        const event = clickInfo.event;

        payload.eventTitle = event.title;
        payload.contract_id = event.extendedProps?.contract_id;
        payload.extendedProps = event.extendedProps;
        payload.start = event.start;
        payload.end = event.end;

        if (event.extendedProps?.ez_table) {
            const titleSplit = event.title.split(':');

            payload.eventTitle = titleSplit.length ? titleSplit[1] : event.title;
            payload.address = titleSplit.length ? titleSplit[0] : event.title;
        }

        setSelectedEvent(payload);
    };

    const _openTransactionDetailTab = (
        event: SelectedEventType,
        contract_id?: string,
        address?: string
    ) => {
        if (HomeCtx.openTransactionDetailTab) {
            HomeCtx.setActiveTabByButton(true);
            HomeCtx.openTransactionDetailTab(
                contract_id || -1,
                address,
                {
                    subTabKey: String(SelectedEvent?.extendedProps?.ez_table).includes('todolist')
                        ? 'ez_detail_tasks'
                        : undefined,
                },
                TAB_CATEGORY.CALENDAR
            );
        }
    };

    const _reloadCalendarEvents = (start_date: string, end_date: string) => {
        const events: any[] = [];

        setIsLoadingCalendarEvents(true);

        Promise.all([
            getCalendarEvents({
                where: {
                    '>::<StartTime': [start_date, end_date],
                    contract_id: TransactionFilter.length ? TransactionFilter : undefined,
                    ez_table_column_name: ColumnTypeFilter
                        ? ColumnTypeFilter.replace('task.', '').replace('date.', '')
                        : undefined,
                },
                pageSize: 5000,
            }),
        ])
            .then(respArray => {
                respArray.forEach(resp => {
                    if (resp && resp.data) {
                        events.push(...resp.data);
                    }
                });
            })
            .finally(() => {
                setEvents(events);
                setIsLoadingCalendarEvents(false);
            });
    };

    const _triggerUpdateCalendarEventsDelayed = debounce(
        (filterType: string, value: string | number | number[]) => {
            switch (filterType) {
                case 'filter.transaction':
                    setTransactionFilter(value as number[]);
                    break;
                case 'filter.column_type':
                    setColumnTypeFilter(value as string);
                    break;

                default:
                    break;
            }
        },
        500
    );

    const EventPopover: React.FC<{
        event: EventApi;
        SetPopoverVisible: React.Dispatch<React.SetStateAction<boolean>>;
    }> = ({ event, SetPopoverVisible }) => {
        const titleSplit = event.title.split(':');

        const eventTitle = titleSplit.length ? titleSplit[1] : event.title;
        const contract_id = event.extendedProps?.contract_id;
        const address = titleSplit.length ? titleSplit[0] : event.title;

        return (
            <div>
                <Row>
                    <Col span={24}>
                        {/* {IsLoadingEventDetail && <EZOverlaySpinner />} */}
                        <Form layout="vertical">
                            <Form.Item label="Title:">{eventTitle || '-'}</Form.Item>
                            <Form.Item label="Listing Address:">
                                <span>
                                    {address || '-'}&nbsp;
                                    <Button
                                        title="Open in a new tab"
                                        // icon={<ArrowsAltOutlined />}
                                        size="small"
                                        className="ml-2 ez-action-btn"
                                        onClick={() => {
                                            SetPopoverVisible(true);
                                            _openTransactionDetailTab(event, contract_id, address);
                                            return false;
                                        }}
                                    >
                                        Open
                                    </Button>
                                </span>
                            </Form.Item>
                            <Form.Item
                                label={
                                    String(event.extendedProps?.ez_table).includes('todolist')
                                        ? 'Due Date:'
                                        : 'Date'
                                }
                            >
                                <EZDateFormat
                                    value={event.start?.toString()}
                                    format="MMM Do, YYYY"
                                />
                            </Form.Item>
                        </Form>
                    </Col>
                </Row>
            </div>
        );
    };

    const CalendarEvent: React.FC<{
        event: EventApi;
    }> = ({ event }) => {
        // const [PopoverVisible, SetPopoverVisible] = useState<boolean>(false);

        return <div>{event.title}</div>;
        // return (
        //     <Popover
        //         trigger={['click']}
        //         content={<EventPopover event={event} SetPopoverVisible={SetPopoverVisible} />}
        //         title="Event Detail"
        //         placement="left"
        //         // getPopupContainer={node =>
        //         //     node.parentElement?.parentElement?.parentElement || document.body
        //         // }
        //         visible={PopoverVisible}
        //         onVisibleChange={visible => {
        //             SetPopoverVisible(visible);
        //         }}
        //         overlayClassName="ez-calendar-popover"
        //     >
        //         <div>{event.title}</div>
        //     </Popover>
        // );
    };

    const renderEventContent = function (eventContent: EventContentArg) {
        return (
            <>
                {/* <CalendarEvent event={eventContent.event} /> */}
                <div>{eventContent.event.title}</div>
            </>
        );
    };

    const _reloadCalendar = () => {
        if (CalendarDuration && CalendarDuration.start_date) {
            _reloadCalendarEvents(CalendarDuration.start_date, CalendarDuration.end_date);
        }
    };

    const _fetchSubUsers = () => {
        getAllTeamMembers().then(({ data }) => {
            setTeamMembers((data as AccountCompactDataType[]) || []);
        });
    };

    const _saveNewTaskData = async (
        contractID: number,
        taskPayload: TransactionTaskItemType,
        formType: 'FULL' | 'PARTIAL' = 'PARTIAL'
    ) => {};

    const _showManageTaskModal = (
        mode: 'EDIT' | 'ADD' = 'ADD',
        startDate?: Date,
        endDate?: Date,
        selectedEventParam?: SelectedEventType
    ) => {
        let contractID =
            mode === 'EDIT' ? selectedEventParam?.extendedProps?.contract_id : undefined;
        const record: Partial<TransactionTaskItemType> = {
            id:
                mode === 'EDIT'
                    ? Number(selectedEventParam?.extendedProps?.ez_table_column_id_val)
                    : 0,
            uuid: 'uuid-dummy',
            list_id: undefined,
            d_created: 0,
            d_completed: 0,
            d_edited: 0,
            compl: 0,
            title: mode === 'EDIT' ? selectedEventParam?.eventTitle?.trim() : '',
            duedate: startDate ? moment(startDate).format('YYYY-MM-DD') : undefined,
            note: '',
            prio: 0,
            ow: 0,
            tags: '',
            tags_ids: '',
            defaulttask_id: 0,
            task_list: {
                name: '',
                contract_id: -1,
            },
        };

        const newTaskDetailModal = Modal.confirm({
            title: (
                <Typography.Title level={4}>
                    {mode === 'ADD' ? 'Add New Task' : 'Edit Task Detail'}
                </Typography.Title>
            ),
            content: (
                <EditTaskModalContent
                    contractAddress={mode === 'EDIT' ? selectedEventParam?.address : undefined}
                    contractID={contractID}
                    task={record}
                    form={form}
                    taskListMenu={[]}
                    mode={mode}
                    subUsers={TeamMembers}
                    source="calendar"
                />
            ),
            okText: 'Save',
            icon: <></>,
            style: { top: '40px' },
            width: '750px',
            onCancel: () => {
                calendarRef.current?.getApi().unselect();
            },
            onOk: async () => {
                newTaskDetailModal.update({
                    okButtonProps: {
                        disabled: true,
                    },
                    okText: 'Saving...',
                });
                return form
                    .validateFields()
                    .then(async values => {
                        const formValues = {
                            ...values,
                            duedate: values.duedate
                                ? moment(values.duedate).format('YYYY-MM-DD')
                                : null,
                        };
                        calendarRef.current?.getApi().unselect();
                        if (mode === 'EDIT') {
                            // Take the contract ID from the selected event when editing a task
                            return updateTask(contractID, record.id || 0, formValues, 'PARTIAL')
                                .then(() => {
                                    showNotification(
                                        'success',
                                        `Task: "${formValues.title}" has been successfully updated.`
                                    );
                                    _reloadCalendar();
                                    newTaskDetailModal.destroy();
                                    if (SelectedEvent) {
                                        SelectedEvent.start = formValues.duedate;
                                        SelectedEvent.eventTitle = formValues.title;
                                        SelectedEvent.end = formValues.duedate;
                                        SelectedEvent.extendedProps = {
                                            ...SelectedEvent.extendedProps,
                                            description: formValues.note,
                                        };
                                        setSelectedEvent({ ...SelectedEvent });
                                    }
                                    return record;
                                })
                                .catch(err => {
                                    throw err;
                                });
                        } else {
                            // Extract contract ID from the selected transaction when adding a new task
                            const selectedEventContractID = formValues.contract_id;
                            return addNewTask(selectedEventContractID, formValues, 'PARTIAL')
                                .then(() => {
                                    showNotification(
                                        'success',
                                        `New task: "${formValues.title}" has been successfully added.`
                                    );
                                    _reloadCalendar();
                                    newTaskDetailModal.destroy();

                                    return formValues;
                                })
                                .catch((err: any) => {
                                    throw err;
                                });
                        }
                    })
                    .catch(err => {
                        newTaskDetailModal.update({
                            okButtonProps: {
                                disabled: false,
                            },
                            okText: 'Save',
                        });
                        calendarRef.current?.getApi().unselect();

                        throw err;
                    });
            },
            closable: true,
            maskClosable: true,
        });
    };

    useEffect(() => {
        if (CalendarDuration && CalendarDuration.start_date) {
            _reloadCalendarEvents(CalendarDuration.start_date, CalendarDuration.end_date);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [CalendarDuration, TransactionFilter, ColumnTypeFilter]);

    useEffect(() => {
        _fetchSubUsers();
    }, []);

    return (
        <div className="ez-tab-calendar">
            <div>
                <div className="pr-3 pl-4 text-help" style={{ textAlign: 'left' }}>
                    <InfoCircleOutlined /> Tip: Select or click the event to see its detail. Click{' '}
                    <Popover
                        content={
                            <Row gutter={[0, 12]} style={{ width: '500px' }}>
                                <Col>
                                    <b>Tip #1</b> - Click any where within the calendar to add a new
                                    task.
                                </Col>
                                <Col>
                                    <b>Tip #2</b> - Choose Filter By to see tasks based on the
                                    transaction's date criteria.
                                </Col>
                            </Row>
                        }
                        title={
                            <>
                                More tips on the <b>Tasks</b> tab.
                            </>
                        }
                        trigger="click"
                        // placement="right"
                    >
                        <a>here</a>
                    </Popover>{' '}
                    to see more tips.
                </div>
            </div>
            <div className="mb-3 ez-sub-content-wrapper">
                <Form>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="transaction_filter" noStyle>
                                <EZSelectTransaction
                                    mode="multiple"
                                    onChange={value =>
                                        _triggerUpdateCalendarEventsDelayed(
                                            'filter.transaction',
                                            value
                                        )
                                    }
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="filter_by" noStyle>
                                <Select
                                    style={{ width: '100%' }}
                                    listHeight={400}
                                    placeholder={
                                        <span>
                                            <Text>Filter By: </Text>
                                            <Text strong>None</Text>
                                        </span>
                                    }
                                    allowClear
                                    // onChange={(value, option) => _setFilterBy(value)}
                                    onChange={value =>
                                        _triggerUpdateCalendarEventsDelayed(
                                            'filter.column_type',
                                            value
                                        )
                                    }
                                >
                                    <OptGroup label="Task">
                                        <Option value="task.duedate">
                                            <span>
                                                <Text>Filter By: </Text>
                                                <Text strong>Task Due Date</Text>
                                            </span>
                                        </Option>
                                    </OptGroup>
                                    <OptGroup label="Important Dates">
                                        <Option value="date.close_date">
                                            <span>
                                                <Text>Filter By: </Text>
                                                <Text strong>Closing Date</Text>
                                            </span>
                                        </Option>
                                        <Option value="date.contract_date">
                                            <span>
                                                <Text>Filter By: </Text>
                                                <Text strong>Contract Date</Text>
                                            </span>
                                        </Option>
                                        <Option value="date.effective_date">
                                            <span>
                                                <Text>Filter By: </Text>
                                                <Text strong>Contract Effective Date</Text>
                                            </span>
                                        </Option>
                                        <Option value="date.listing_date">
                                            <span>
                                                <Text>Filter By: </Text>
                                                <Text strong>Listing Date</Text>
                                            </span>
                                        </Option>
                                        <Option value="date.expiration_date">
                                            <span>
                                                <Text>Filter By: </Text>
                                                <Text strong>Listing Expiration Date</Text>
                                            </span>
                                        </Option>
                                        <Option value="date.inspection_contingency_date">
                                            <span>
                                                <Text>Filter By: </Text>
                                                <Text strong>Inspection Contingency Date</Text>
                                            </span>
                                        </Option>
                                        <Option value="date.financing_contingency_date">
                                            <span>
                                                <Text>Filter By: </Text>
                                                <Text strong>Financing Contingency Date</Text>
                                            </span>
                                        </Option>
                                        <Option value="date.appraisal_contingency_date">
                                            <span>
                                                <Text>Filter By: </Text>
                                                <Text strong>Appraisal Contingency Date</Text>
                                            </span>
                                        </Option>
                                        <Option value="date.val_date">
                                            <span>
                                                <Text>Filter By: </Text>
                                                <Text strong>Custom Contingency Dates</Text>
                                            </span>
                                        </Option>
                                    </OptGroup>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </div>
            <div>
                <div className="">
                    <Row gutter={16}>
                        <Col span={SelectedEvent ? 18 : 24}>
                            {/* <Col span={SelectedEvent ? 18 : 24}> */}
                            <div className="ez-sub-content-wrapper" style={{ minHeight: '1100px' }}>
                                <Row>
                                    <Col span={24}>
                                        {IsLoadingCalendarEvents && <EZOverlaySpinner />}
                                        <FullCalendar
                                            // contentHeight={'150px'}
                                            ref={calendarRef}
                                            customButtons={{
                                                refreshButton: {
                                                    text: '🔄 Reload',
                                                    click: () => {
                                                        if (
                                                            CalendarDuration &&
                                                            CalendarDuration.start_date
                                                        ) {
                                                            _reloadCalendarEvents(
                                                                CalendarDuration.start_date,
                                                                CalendarDuration.end_date
                                                            );
                                                        }
                                                    },
                                                },
                                                addNewEventButton: {
                                                    text: '+ Add New Task',
                                                    click: () => {
                                                        _showManageTaskModal('ADD');
                                                    },
                                                },
                                                syncCalendarButton: {
                                                    // Add icon
                                                    text: '📅 Sync Calendar(s)',
                                                    click: () => {
                                                        syncAllIntegratedCalendars().then(resp => {
                                                            showNotification(
                                                                'info',
                                                                'Please wait for a few minutes then hit the "Reload" button to refresh the calendar.',
                                                                'Syncing calendar(s) now... '
                                                            );
                                                        });
                                                    },
                                                },
                                            }}
                                            dayMaxEventRows={true}
                                            views={{
                                                dayGrdayGridMonthid: {
                                                    dayMaxEventRows: 3,
                                                },
                                            }}
                                            plugins={[
                                                dayGridPlugin,
                                                timeGridPlugin,
                                                interactionPlugin,
                                            ]}
                                            headerToolbar={{
                                                left: 'prev,next today',
                                                center: 'title',
                                                right: 'dayGridMonth,dayGridWeek,timeGridDay addNewEventButton syncCalendarButton refreshButton',
                                            }}
                                            initialView="dayGridMonth"
                                            editable={false}
                                            selectable={true}
                                            select={arg => {
                                                _showManageTaskModal('ADD', arg.start, arg.end);
                                            }}
                                            selectMirror={true}
                                            dayMaxEvents={true}
                                            weekends={CalendarState.weekendsVisible}
                                            dayCellClassNames={arg => {
                                                // arg.date is a JS Date object
                                                const day = arg.date.getDay();
                                                if (day === 0 || day === 6) {
                                                    // Sunday = 0, Saturday = 6
                                                    return ['fc-weekend-cell'];
                                                }
                                                return [];
                                            }}
                                            events={Events}
                                            // eventContent={renderEventContent} // custom render function
                                            eventClick={handleEventClick}
                                            datesSet={arg => {
                                                setCalendarDuration({
                                                    start_date: arg.startStr,
                                                    end_date: arg.endStr,
                                                });
                                            }}
                                        />
                                    </Col>
                                </Row>
                            </div>
                        </Col>
                        {SelectedEvent && (
                            <Col span={6}>
                                <div
                                    className="ez-sub-content-wrapper"
                                    // style={{ height: '100%', paddingTop: '20px' }}
                                    style={{
                                        position: 'sticky',
                                        top: '60px',
                                        zIndex: 50,
                                        // height: '100%',
                                        paddingTop: '20px',
                                    }}
                                >
                                    <Row
                                        className="mb-4 pb-4 border-bottom"
                                        align="middle"
                                        justify="space-between"
                                    >
                                        <Col span={12}>
                                            <Typography.Title level={4} className="mb-0">
                                                Event Detail
                                            </Typography.Title>
                                        </Col>
                                        <Col span={12} style={{ textAlign: 'right' }}>
                                            <Button
                                                title="Edit"
                                                size="small"
                                                className="ez-action-btn mr-2"
                                                onClick={() => {
                                                    if (
                                                        SelectedEvent?.extendedProps?.ez_table ===
                                                        'task_todolist'
                                                    ) {
                                                        _showManageTaskModal(
                                                            'EDIT',
                                                            undefined,
                                                            undefined,
                                                            SelectedEvent
                                                        );
                                                    } else {
                                                        showNotification(
                                                            'error',
                                                            'This is not a task. Please select a task to edit.'
                                                        );
                                                    }
                                                }}
                                            >
                                                Edit
                                            </Button>
                                            <Button
                                                title="Delete"
                                                size="small"
                                                className="mr-4"
                                                danger
                                                onClick={() => {
                                                    if (
                                                        SelectedEvent?.extendedProps?.ez_table ===
                                                        'task_todolist'
                                                    ) {
                                                        Modal.confirm({
                                                            title: 'Delete Task',
                                                            content: (
                                                                <div>
                                                                    Are you sure you want to delete
                                                                    this task -{' '}
                                                                    <b>
                                                                        {SelectedEvent?.eventTitle}
                                                                    </b>
                                                                    ? This action cannot be undone.
                                                                </div>
                                                            ),
                                                            okText: 'Delete',
                                                            cancelText: 'Cancel',
                                                            onOk: () => {
                                                                const contractID =
                                                                    SelectedEvent?.extendedProps
                                                                        ?.contract_id;
                                                                const taskID =
                                                                    SelectedEvent?.extendedProps
                                                                        ?.ez_table_column_id_val;
                                                                if (contractID && taskID) {
                                                                    deleteTask(contractID, taskID)
                                                                        .then(() => {
                                                                            setSelectedEvent(
                                                                                undefined
                                                                            );
                                                                            _reloadCalendar();
                                                                            showNotification(
                                                                                'success',
                                                                                'Task has been successfully deleted.'
                                                                            );
                                                                        })
                                                                        .catch(err => {
                                                                            showNotification(
                                                                                'error',
                                                                                'Failed to delete task.'
                                                                            );
                                                                        });
                                                                }
                                                            },
                                                        });
                                                    } else {
                                                        showNotification(
                                                            'error',
                                                            'This is not a task. Please select a task to delete.'
                                                        );
                                                    }
                                                }}
                                            >
                                                <DeleteOutlined />
                                            </Button>
                                            <CloseOutlined
                                                className="cursor-pointer"
                                                onClick={() => setSelectedEvent(undefined)}
                                            />
                                        </Col>
                                    </Row>
                                    {SelectedEvent ? (
                                        <Row>
                                            <Col span={24}>
                                                {/* {IsLoadingEventDetail && <EZOverlaySpinner />} */}
                                                <Form layout="vertical">
                                                    <Form.Item label="Title:">
                                                        {SelectedEvent?.eventTitle || '-'}
                                                    </Form.Item>
                                                    {SelectedEvent?.extendedProps?.ez_table && (
                                                        <Form.Item label="Listing Address:">
                                                            <span>
                                                                {SelectedEvent?.address || '-'}
                                                                <Button
                                                                    title="Open in a new tab"
                                                                    // icon={<ArrowsAltOutlined />}
                                                                    size="small"
                                                                    className="ez-action-btn ml-2"
                                                                    onClick={() =>
                                                                        _openTransactionDetailTab(
                                                                            SelectedEvent,
                                                                            SelectedEvent.contract_id,
                                                                            SelectedEvent.address
                                                                        )
                                                                    }
                                                                >
                                                                    Open <ExpandAltOutlined />
                                                                </Button>
                                                            </span>
                                                        </Form.Item>
                                                    )}
                                                    <Form.Item
                                                        label={
                                                            String(
                                                                SelectedEvent?.extendedProps
                                                                    ?.ez_table
                                                            ).includes('todolist')
                                                                ? 'Due Date:'
                                                                : 'Date:'
                                                        }
                                                    >
                                                        <EZDateFormat
                                                            value={SelectedEvent?.start?.toString()}
                                                            format={
                                                                moment(
                                                                    SelectedEvent?.start
                                                                ).hour() === 0 &&
                                                                moment(
                                                                    SelectedEvent?.start
                                                                ).minute() === 0
                                                                    ? 'MMM Do, YYYY'
                                                                    : 'MMM Do, YYYY [@] hh:mm a'
                                                            }
                                                        />
                                                    </Form.Item>
                                                    <Form.Item label="Description:">
                                                        <Text>
                                                            {SelectedEvent?.extendedProps
                                                                ?.description || '-'}
                                                        </Text>
                                                    </Form.Item>
                                                    <Form.Item label="Type:">
                                                        <Text>
                                                            {SelectedEvent?.extendedProps
                                                                ?.ez_table === 'task_todolist'
                                                                ? 'Task'
                                                                : 'Date/Reminder'}
                                                        </Text>
                                                    </Form.Item>
                                                    <Form.Item label="Calendar:">
                                                        <Text>
                                                            {SelectedEvent?.extendedProps
                                                                ?.calendar_name || 'Internal'}
                                                        </Text>
                                                    </Form.Item>
                                                </Form>
                                            </Col>
                                        </Row>
                                    ) : (
                                        <Alert
                                            message="No event is selected"
                                            type="info"
                                            showIcon
                                        />
                                    )}
                                </div>
                            </Col>
                        )}
                    </Row>
                </div>
            </div>
        </div>
    );
}
